import { useNavigate } from "react-router-dom";
import { Hero } from "./Hero";
import {
  HowItWorks,
  ByteBoundary,
  ScoreboardPreview,
  DiscordCosts,
  MediaEducation,
  Roadmap,
} from "./Sections";
import { Pricing } from "./Pricing";
import { RoiCaseStudy } from "./RoiCaseStudy";
import { LegalDoc } from "./LegalDoc";
import { WaitlistForm } from "./WaitlistForm";
import privacyMd from "../../content/legal/privacy.md?raw";
import termsMd from "../../content/legal/terms.md?raw";

type CTA = { onGetStarted: () => void };

// ── Home: short & punchy — hero, three highlights, one CTA to Pricing ─────────
export function HomePage({ onGetStarted }: CTA) {
  return (
    <>
      <Hero />
      <FactsStrip />
      <WaitlistBand />
      <CtaBand onGetStarted={onGetStarted} />
    </>
  );
}

// ── Legal + contact ───────────────────────────────────────────────────────────
export function PrivacyPage() {
  return <LegalDoc markdown={privacyMd} />;
}
export function TermsPage() {
  return <LegalDoc markdown={termsMd} />;
}
export function ContactPage() {
  return (
    <section className="section">
      <div className="container" style={{ maxWidth: 620 }}>
        <span className="eyebrow">Get in touch</span>
        <h1 style={{ margin: "12px 0 8px", fontSize: "clamp(1.9rem, 3.4vw, 2.6rem)" }}>
          Join the waitlist
        </h1>
        <p className="lede" style={{ marginBottom: 24 }}>
          Drop your email and we’ll be in touch — early access, a walkthrough, or
          answers to any questions. No spam, and no charge to get an exact quote.
        </p>
        <WaitlistForm source="contact" />
      </div>
    </section>
  );
}

// ── Pricing: all the money content ────────────────────────────────────────────
export function PricingPage({ onGetStarted }: CTA) {
  return (
    <>
      <Pricing onGetStarted={onGetStarted} />
      <MediaEducation />
      <RoiCaseStudy />
      <DiscordCosts />
    </>
  );
}

// ── How it works: the flow + live status + roadmap ────────────────────────────
export function HowItWorksPage() {
  return (
    <>
      <HowItWorks />
      <ScoreboardPreview />
      <Roadmap />
    </>
  );
}

// ── Security: predictable price, isolation, live ──────────────────────────────
export function SecurityPage() {
  return <ByteBoundary />;
}

// ── Shared page pieces ────────────────────────────────────────────────────────

function FactsStrip() {
  return (
    <div className="container">
      <div className="card stripe-facts" style={{ borderRadius: 22, overflow: "hidden" }}>
        <div className="fact">
          <b>Every byte kept</b>
          <span>Messages, threads, reactions and files land in Discord exactly as they were.</span>
        </div>
        <div className="fact">
          <b>Invite &amp; watch</b>
          <span>No engineering. Add the bot, pick channels, watch it stream across live.</span>
        </div>
        <div className="fact">
          <b>Yours to keep</b>
          <span>Your history lives on in a platform you own — searchable by your team and your AI.</span>
        </div>
      </div>
    </div>
  );
}

function WaitlistBand() {
  return (
    <section className="section" style={{ paddingBlock: "clamp(28px, 4vw, 52px)" }}>
      <div className="container">
        <div className="card waitlist-band">
          <div className="waitlist-band-copy">
            <span className="eyebrow">Not ready to connect?</span>
            <h3>Join the waitlist</h3>
            <p className="muted">
              Drop your email — we’ll reach out with early access and answer any
              questions. No spam.
            </p>
          </div>
          <WaitlistForm source="home" compact />
        </div>
      </div>
    </section>
  );
}

function CtaBand({ onGetStarted }: CTA) {
  const navigate = useNavigate();
  return (
    <section className="section">
      <div className="container">
        <div
          className="card"
          style={{
            padding: "clamp(36px, 6vw, 64px)",
            textAlign: "center",
            background: "var(--conduit-soft)",
          }}
        >
          <span className="eyebrow" style={{ justifyContent: "center", marginBottom: 16 }}>
            In the age of AI, your data is the asset
          </span>
          <h2 className="h-section" style={{ maxWidth: "26ch", margin: "0 auto 14px" }}>
            Migrate everything — your history and your team — then cancel Slack for
            good.
          </h2>
          <p className="lede" style={{ margin: "0 auto 26px" }}>
            Move every message and file, bring your whole team over, and keep the
            record your team built. Keep the data, drop the bill.
          </p>
          <div className="hero-cta" style={{ justifyContent: "center" }}>
            <button className="btn btn-primary" onClick={() => navigate("/pricing")}>
              See pricing &amp; estimate
            </button>
            <button className="btn btn-ghost" onClick={onGetStarted}>
              Join the waitlist
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
